import { Injectable } from '@angular/core';

import { SurveyBhUserGrpcContractService } from '@lyra/app/core/api/grpc';
import { EnumHelper } from '@lyra/app/core/helpers/enum.helper';
import { ErrorService } from '@lyra/app/core/services/error.service';
import { SET_QUESTIONS_COUNT, SET_SURVEY } from '@lyra/app/core/store/actions/plan.actions';
import { SettingSelectService } from '@lyra/app/core/store/selectors';
import { BhApi } from '@mark/web-contracts';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { Survey } from '../../classes/survey.class';
import { RouteProgress } from '../../enums/route-progress';
import * as medicalQuestionsActions from '../actions/medical-questions.actions';
import { GET_PILLS_COMMAND } from '../actions/medications.actions';
import { SET_PROGRESS } from '../actions/questionnaire.actions';
import * as actions from '../actions/questionnaire.actions';
import * as questionsActions from '../actions/questions.actions';
import * as womanHealthActions from '../actions/woman-health.actions';
import { SurveySelectService, UserSelectService, WomanHealthSelectService } from '../selectors';
import { MedicalQuestionsSelectService } from '../selectors/medical-questions.selector';
import { QuestionnaireSelectService } from '../selectors/questionnaire.selector';
import { QuestionsSelectService } from '../selectors/questions.selector';
import { IQuestionnaireState } from '../states/questionnaire.state';
@Injectable()
export class QuestionnaireEffects {
  // init$ = createEffect(() =>
  //   this._actions$.pipe(
  //     ofType(actions.INIT_APP_COMMAND),
  //     switchMap(() =>
  //       this._appGrpcContractService.getAppData().pipe(
  //         map((settings) => actions.INIT_APP_SUCCESS({ settings })),
  //         catchError((response) => this._errorService.handleApiError(response)),
  //       ),
  //     ),
  //   ),
  // );

  // initSuccess$ = createEffect(() =>
  //   zip(
  //     this._actions$.pipe(ofType(actions.INIT_APP_COMMAND)),
  //     this._actions$.pipe(ofType(actions.INIT_APP_SUCCESS)),
  //   ).pipe(map(() => actions.INITIALIZED())),
  // );

  back$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.BACK),
        filter((x) => x.target === undefined),
        withLatestFrom(
          this._questionnaireSelect.getRoute(),
          this._questionsSelect.getCount(),
          this._medicalQuestionsSelect.getCount(),
          this._womanHealthSelect.getCount(),
          this._userSelect.getMainUserData(),
          this._settingSelect.getReferrerGoals(),
          this._questionnaireSelect.getType(),
        ),
        tap(([, route, questions, medicalQuestions, womanHealth, { GenderType }, referrerGoals, type]) => {
          switch (route) {
            case 'name':
              this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'gender' }));
              break;
            case 'birthday':
              // short-quiz skips the name screen, so back from birthday returns to gender.
              this._store$.dispatch(actions.CHANGE_ROUTE({ route: type === 'short-quiz' ? 'gender' : 'name' }));
              break;
            case 'goals':
              this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'birthday' }));
              break;
            case 'woman-health':
              if (womanHealth.current === 1) {
                if (referrerGoals) {
                  this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'birthday' }));
                } else {
                  this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'goals' }));
                }
              } else {
                this._store$.dispatch(womanHealthActions.MOVE_TO_PREVIOUSE_QUESTION());
              }
              break;
            case 'medical-questions':
              if (medicalQuestions.current === 1) {
                if (GenderType === BhApi.GenderType.GenderType_Female) {
                  this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'woman-health' }));
                } else {
                  if (referrerGoals) {
                    this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'birthday' }));
                  } else {
                    this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'goals' }));
                  }
                }
              } else {
                this._store$.dispatch(medicalQuestionsActions.MOVE_TO_PREVIOUSE_QUESTION());
              }
              break;
            case 'questions':
              if (questions.current === 1) {
                this._store$.dispatch(actions.CHANGE_ROUTE({ route: 'medical-questions' }));
              } else {
                this._store$.dispatch(questionsActions.MOVE_TO_PREVIOUSE_QUESTION());
              }
              break;
            default:
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  routeChange$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.CHANGE_ROUTE),
        withLatestFrom(this._questionnaireSelect.getTotalSteps()),
        tap(([{ route }, totalQuestions]) => {
          switch (route) {
            case 'gender':
              this._store$.dispatch(SET_PROGRESS({ percent: EnumHelper.valueByKey(RouteProgress, route) }));
              break;
            case 'birthday':
              this._store$.dispatch(SET_PROGRESS({ percent: EnumHelper.valueByKey(RouteProgress, route) }));
              break;
            case 'goals':
              this._store$.dispatch(SET_PROGRESS({ percent: EnumHelper.valueByKey(RouteProgress, route) }));
              break;
            case 'woman-health':
              this._store$.dispatch(SET_PROGRESS({ percent: EnumHelper.valueByKey(RouteProgress, route) }));
              break;
            case 'medical-questions':
              this._store$.dispatch(SET_PROGRESS({ percent: EnumHelper.valueByKey(RouteProgress, route) }));
              break;
            case 'questions':
              this._store$.dispatch(SET_PROGRESS({ percent: EnumHelper.valueByKey(RouteProgress, route) }));
              break;
            case 'medications':
              this._store$.dispatch(SET_PROGRESS({ percent: EnumHelper.valueByKey(RouteProgress, route) }));
              this._store$.dispatch(GET_PILLS_COMMAND());
              this._store$.dispatch(SET_QUESTIONS_COUNT({ count: totalQuestions }));
              break;
            default:
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  onMedicalQuestions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.CHANGE_ROUTE),
      filter((x) => x.route === 'medical-questions'),
      withLatestFrom(this._medicalQuestionsSelect.hasQuestions()),
      filter(([, hasMedicalQuestions]) => hasMedicalQuestions),
      map(() => ({
        startPercent: EnumHelper.valueByKey(RouteProgress, 'medical-questions'),
        maxPercent: EnumHelper.valueByKey(RouteProgress, 'questions'),
      })),
      switchMap(({ startPercent, maxPercent }) =>
        this._medicalQuestionsSelect.getCount().pipe(
          filter((x) => !!x.count && x.count > 0),
          map(({ count, current }) => {
            const percent = (maxPercent - startPercent) / count!;
            return SET_PROGRESS({ percent: startPercent + percent * (current! - 1) });
          }),
        ),
      ),
    ),
  );

  onQuestions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.CHANGE_ROUTE),
      filter((x) => x.route === 'questions'),
      map(() => ({
        startPercent: EnumHelper.valueByKey(RouteProgress, 'questions'),
        maxPercent: EnumHelper.valueByKey(RouteProgress, 'medications'),
      })),
      switchMap(({ startPercent, maxPercent }) =>
        this._questionsSelect.getCount().pipe(
          filter((x) => !!x.count && x.count > 0),
          map(({ count, current }) => {
            const percent = (maxPercent - startPercent) / count!;
            return SET_PROGRESS({ percent: startPercent + percent * current! - 1 });
          }),
        ),
      ),
    ),
  );

  onResult$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.CALCULATE_AND_GET_RESULTS_COMMAND),
      switchMap(() =>
        this._surveyBhUserGrpcContractService.getLastCompleted().pipe(
          tap((survey) => {
            this._store$.dispatch(SET_SURVEY({ survey: new Survey(survey), withRedirection: false }));
          }),
          map((survey) => actions.CALCULATE_AND_GET_RESULTS_SUCCESS({ survey: new Survey({ Id: survey?.Id }) })),
          catchError((response) =>
            this._errorService.handleApiError(response, 'SurveyBhUserWebGrpcContractClient.GetLastCompleted'),
          ),
        ),
      ),
    ),
  );

  onResultSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.CALCULATE_AND_GET_RESULTS_SUCCESS),
      map(() => actions.CHANGE_ROUTE({ route: 'medications' })),
    ),
  );

  constructor(
    private _store$: Store<IQuestionnaireState>,
    private _actions$: Actions,
    private _questionnaireSelect: QuestionnaireSelectService,
    private _questionsSelect: QuestionsSelectService,
    private _medicalQuestionsSelect: MedicalQuestionsSelectService,
    private _womanHealthSelect: WomanHealthSelectService,
    private _userSelect: UserSelectService,
    private _surveySelect: SurveySelectService,
    private _surveyBhUserGrpcContractService: SurveyBhUserGrpcContractService,
    private _errorService: ErrorService,
    private _settingSelect: SettingSelectService,
  ) {}
}
